import { useState, useRef, useEffect } from "react";
import {
  HumanMembersSection,
  AITeamSection,
  SystemTeamSection,
  InviteCodeSection,
  EmailInviteModal,
  ConnectYourAiSection,
  MyDaemonKeySection,
  DirectiveSigningKeySection,
} from "./TeamArea";
import type { CreatedKey } from "./TeamArea";
import easyRelaySkillContent from "../../docs/relay-skill-default.md?raw";

interface TeamPageProps {
  workspaceId?: string;
  onOpenControlCenter?: () => void;
  onScrollReady?: (el: HTMLDivElement | null) => void;
}

// track:skills-retirement-relay (decision #49035, spec #49036 Slice 2) —
// relocated from the now-deleted SkillsPanel.tsx. This is the only piece of
// the old Skills tab that keeps its value: a copy-paste prompt for the
// human's own external AI chat, unrelated to the in-app skills/spawner
// system being retired. Exported (round 2) so it's independently testable
// without mounting the rest of TeamPage's API-heavy TeamArea sections.
export function EasyRelaySkillPanel() {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(easyRelaySkillContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API unavailable — button label stays "Copy" so the user knows it didn't work.
    }
  };

  return (
    <div className="sc-info-card">
      <div className="sc-info-card-header">
        <div className="sc-teal-bar" />
        <h3>Easy Relay Skill — Install this in your AI chat (Claude.ai, Gemini, ChatGPT)</h3>
      </div>
      <div style={{ padding: "12px 18px" }}>
        <p style={{ fontFamily: "var(--font-ui)", fontSize: 12, color: "var(--ink-soft)", lineHeight: 1.55, marginBottom: 12 }}>
          This is for your <strong>human planner</strong> — the AI chat you think out loud with —
          not for spawned dev agents. Paste it as a project instruction, custom GPT system prompt,
          or Gemini gem instructions. It keeps directives to the team routed through Relay instead
          of getting stuck in a chat only you can see.
        </p>
        <button className="btn primary" onClick={handleCopy}>
          {copied ? "Copied ✓" : "Copy Easy Relay Skill"}
        </button>
      </div>
    </div>
  );
}

export function TeamPage({ workspaceId = "default", onScrollReady }: TeamPageProps) {
  const [showInviteModal, setShowInviteModal] = useState(false);
  const scrollElRef = useRef<HTMLDivElement>(null);
  const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);

  useEffect(() => {
    if (onScrollReady) onScrollReady(scrollElRef.current);
    return () => { if (onScrollReady) onScrollReady(null); };
  }, [onScrollReady]);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <div style={{ padding: "16px 24px 12px", borderBottom: "1px solid #5FC4C033", flexShrink: 0 }}>
        <div className="sc-title-row">
          <span className="sc-title">Team</span>
        </div>
      </div>
      <div
        ref={scrollElRef}
        className="sc-scroll"
        style={{
          padding: "24px",
          overflowY: "auto",
          flex: 1,
          minHeight: 0,
        }}
      >
      {/* Section 0 — Connect your AI: Relay Helper key, personal + workspace MCP URLs, masked */}
      <ConnectYourAiSection workspaceId={workspaceId} created={createdKey} />

      {/* track:skills-retirement-relay — relocated from the old Skills tab. */}
      <div id="team-section-easy-relay" style={{ marginBottom: 16 }}>
        <EasyRelaySkillPanel />
      </div>

      {/* track:workspace-key-onboarding (decision #48248) — personal daemon
          key, every member, self-service. Separate from the Lead-only
          shared URL above. (MyDaemonKeySection owns its own section id.)
          The new key is shown in the Connect your AI area. */}
      <MyDaemonKeySection workspaceId={workspaceId} onCreated={setCreatedKey} />

      {/* track:workspace-directive-keys-a (spec #48992 Slice A, decision
          #48907) — workspace-shared directive signing key, viewable by
          any confirmed member, Lead-only regenerate. */}
      <DirectiveSigningKeySection workspaceId={workspaceId} />

      {/* Section 1 — Human Team */}
      <div id="team-section-human">
        <HumanMembersSection
          workspaceId={workspaceId}
          onInviteClick={() => setShowInviteModal(true)}
        />
      </div>

      <div id="team-section-invite">
        <InviteCodeSection workspaceId={workspaceId} />
      </div>

      {/* Section 2 — AI Team */}
      <div id="team-section-ai">
        <AITeamSection workspaceId={workspaceId} />
      </div>

      {/* Section 3 — System Team */}
      <div id="team-section-system">
        <SystemTeamSection workspaceId={workspaceId} />
      </div>

      {showInviteModal && (
        <EmailInviteModal
          workspaceId={workspaceId}
          onClose={() => setShowInviteModal(false)}
        />
      )}
      </div>
    </div>
  );
}
